import { Languages } from '@wicadu/langscope'

import { PagesId } from '@ts-enums/PagesId'
import { ContentType } from '@ts-types/ArticleSection'
import { Translations } from '@ts-types/Translations'
import { getUrlByPageId } from '@utils/getUrlByPageId'

const globalTranslations: Translations = {
  es: {
    seo_title: 'Establecer precios de tus productos',
    seo_description: 'Aprende a establecer precios adecuados para tus productos y maximiza tus ventas y ganancias. Descubre estrategias de precios efectivas, pasos para configurarlos y problemas comunes a evitar con wicadu.',
    seo_keywords: 'fijar precios de productos, estrategias de precios, gestión de precios de venta, precios en inventario, consejos para precios, cómo poner precio a productos, ajuste de precios, errores en precios, precios para tienda en línea, guía de precios wicadu, wicadu, asistente de wicadu, tienda en linea',
    page_id: PagesId.PricingYourProducts,
    breadcrumbs: {
      home: 'Inicio',
      items_home: 'Productos e inventario',
      pricing_your_products: 'Establecer precios de tus productos',
    },
    article: {
      title: 'Establecer precios de tus productos',
      intended: [
        `<strong>Propietarios</strong> o <strong>Administradores</strong>  con acceso completo para gestionar el <em>inventario</em> y establecer o actualizar <em>precios de venta</em> <a href=${process.env.STORES_WEBAPP_HOST} title="Asistente de wicadu">Asistente de wicadu</a>.`,
        `<strong>Vendedores</strong> o <strong>Abastecedor</strong> con permisos para <em>configurar precios de productos</em> y <em>realizar ajustes de precios</em> según las necesidades del negocio.`,
      ],
      about: {
        title: 'Acerca de establecer precios de tus productos',
        content_type: ContentType.Paragraph,
        content: [
          'Configurar los <strong>precios de venta</strong> de tus productos te permite definir cuánto cobrarás a tus clientes por cada producto en tu inventario. Este proceso es clave para <em>gestionar tus ventas</em> y <em>calcular márgenes de ganancia</em>. En <strong>wicadu</strong>, los <strong>precios de venta</strong> se establecen una vez para cada producto, con la opción de ajustarlos si es necesario.',
          'Establecer <strong>precios de venta adecuados</strong> te ayudará a atraer más clientes y aumentar tus ganancias. En <strong>wicadu</strong>, puedes ajustar fácilmente los precios de tus productos en cualquier momento, para adaptarte a cambios del mercado o a promociones especiales. Así, tendrás mayor control sobre tu inventario y podrás potenciar tu negocio de forma sencilla y eficaz.'
        ],
      },
      before_begin: {
        title: 'Antes de empezar',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Antes de configurar los precios de venta de tus productos, asegúrate de lo siguiente:'
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<strong>Verifica que tus productos estén correctamente registrados</strong>, incluyendo nombre, código, categoría y otros detalles clave en tu inventario.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<strong>Recuerda que el costo unitario se asigna automáticamente</strong> a través de las compras, pero el precio de venta debe establecerse manualmente por producto.'
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<strong>Define tu estrategia de precios</strong> considerando los costos de adquisición, márgenes de ganancia deseados y condiciones del mercado.'
                ]
              },
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              'Estos pasos previos te permitirán establecer precios de venta de manera rápida y precisa en tu inventario.'
            ],
          },
        ],
      },
      strategies: {
        title: 'Estrategias para fijar los precios de tus productos',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Fijar correctamente el <strong>precio de venta</strong> es clave para garantizar la rentabilidad de tu negocio. Al hacerlo, considera el <strong>valor adicional que ofreces</strong> a tus clientes: el tiempo, esfuerzo y recursos invertidos en desarrollar tu producto, empaquetar o brindar una excelente atención al cliente. Todo esto puede justificar un precio más alto. También es importante tener en cuenta <strong>gastos operativos</strong> como el marketing, tu plan de suscripción, herramientas que utilices y comisiones por venta. Asegúrate de que el precio cubra estos costos y genere un margen de ganancia adecuado.',
              'Además, puedes aplicar <strong>estrategias prácticas como los precios psicológicos</strong> (por ejemplo, $9.990 en lugar de $10.000), que influyen en la percepción del cliente. Revisa también los <strong>precios de tu competencia</strong> para tener una referencia del mercado, y ajusta tus precios según la <strong>temporada o la demanda</strong>. Estas prácticas te ayudarán a mantener tus precios competitivos, mejorar tus ventas y aumentar la percepción de valor de tu marca.'
            ],
          },
        ],
      },
      how_to: {
        title: 'Pasos para establecer precios de tus productos',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Ir al módulo de Inventario: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `Accede a la funcionalidad de <em>Inventario</em> dentro del <a href=${process.env.STORES_WEBAPP_HOST} title="Asistente wicadu">Asistente de wicadu</a>.`,
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Selecciona un producto: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Usa el buscador o navega por la lista para encontrar el producto al que deseas <em>asignar un precio de venta</em>. Haz clic en su nombre para abrir sus detalles'
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Inicia la asignación de precio:'],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Si el producto no tiene un precio asignado, verás el botón <b>"Asignar"</b> en la sección de precio. Haz clic en este botón para activar el modo de edición.'
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Ingresa el precio de venta:'],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Se desplegará un campo donde podrás ingresar el valor de venta del producto.'
                ],
              },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  '<strong>Nota:</strong> Si tu tienda tiene múltiples monedas, aparecerá un selector para elegir la moneda. El soporte multimoneda no se encuentra disponible en los planes gratuitos.'
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Guarda los cambios:'],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Haz clic en <b>"Guardar"</b> para aplicar el precio. Una vez asignado, el producto estará listo para mostrarse en tu tienda y podrá ser vendido sin problemas.'
                ],
              },
            ],
          },
        ],
      },
      next_steps: {
        title: 'Siguientes pasos',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Una vez que has asignado precios de venta a tus productos, puedes seguir optimizando tu tienda con estas acciones recomendadas:',
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Revisa productos sin precio asignado: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Asegúrate de que todos los productos en tu inventario tengan un <strong>precio de venta</strong> configurado para que puedan mostrarse correctamente en la tienda y estar <strong>disponibles para la venta</strong>.'
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Registra costos con órdenes de compra:'],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Registra las compras realizadas para actualizar automáticamente el costo unitario de tus productos y mantener tu inventario al día. Aprende cómo hacerlo en 
                        <a href=${getUrlByPageId(PagesId.CreateManualStoreOrders, Languages.Spanish)} title="Guia para crear un pedido manual en wicadu">esta guía sobre órdenes de compra</a>`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Realiza tu primera venta:'],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Comienza a vender tus productos una vez que tengan precio asignado para que tus clientes puedan comprarlos en la tienda. <em>Descubre cómo hacerlo aquí:
                      <a href=${getUrlByPageId(PagesId.CreateManualUserOrders, Languages.Spanish)} title="Guia para crear ventas wicadu">Cómo crear un pedido</a></em>`,
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      common_issues: {
        title: 'Problemas comunes',
        content_type: ContentType.UnorderedList,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>El precio no se aplica a nuevos pedidos</strong>: Revisa si el producto tiene precios antiguos almacenados en pedidos activos. Actualiza manualmente si es necesario.',
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>No puedo seleccionar otras monedas</strong>: La opción de manejar múltiples monedas está disponible solo en planes pagos. Si estás en un plan gratuito, esta funcionalidad no estará habilitada en tu tienda.',
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>El precio actualizado no se refleja en la tienda en línea</strong>: Espera unos minutos, ya que los cambios pueden tardar en sincronizarse.',
            ],
          },
        ],
      },
    },
  },
  en: {
    seo_title: 'Pricing your products',
    seo_description: 'Learn how to set the right product prices to boost your sales and maximize profits. Discover effective pricing strategies, step-by-step setup, and common pricing issues to avoid with wicadu.',
    seo_keywords: 'product pricing, set product prices, pricing strategies, sales price management, inventory pricing, pricing tips, how to price products, product price adjustment, pricing mistakes, online store pricing, wicadu pricing guide, wicadu, online stores, wicadu Assistant',
    page_id: PagesId.PricingYourProducts,
    breadcrumbs: {
      home: 'Home',
      items_home: 'Products and inventory',
      pricing_your_products: 'Pricing your products',
    },
    article: {
      title: 'Pricing your products',
      intended: [
        `<strong>Store Owners</strong> or <strong>Admins</strong> with full access to manage <em>inventory</em> and set or update <em>product pricing</em> using the <a href=${process.env.STORES_WEBAPP_HOST} title="wicadu Assistant">wicadu Assistant</a>.`,
        `<strong>Sellers</strong> or <strong>Suppliers</strong> with permission to <em>configure product prices</em> and <em>adjust pricing</em> based on business needs.`,
      ],
      about: {
        title: 'About pricing your products',
        content_type: ContentType.Paragraph,
        content: [
          'Setting the <strong>selling price</strong> of your products allows you to define how much customers will pay for each item in your inventory. This step is essential for <em>managing your sales</em> and calculating <em>profit margins</em>. In <strong>wicadu</strong>, each product has a price that you can assign once and update whenever needed.',
          "By assigning the right <strong>selling price</strong>, you can attract more customers and boost your profits. With <strong>wicadu</strong>, it's easy to update product prices anytime — whether you are adjusting to market trends or running special promotions. This gives you greater control over your inventory and helps you grow your business efficiently and effectively."
        ],
      },
      before_begin: {
        title: 'Before you begin',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Before setting the <strong>selling prices</strong> of your products, make sure to check the following:'
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<strong>Ensure your products are properly registered</strong>, including name, SKU, category, and other key details in your inventory.'
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<strong>Remember that the unit cost is automatically assigned</strong> through purchase records, but the selling price must be manually set for each product.'
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<strong>Define your pricing strategy</strong> by considering acquisition costs, desired profit margins, and market conditions.'
                ]
              },
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              'Following these steps will help you set selling prices quickly and accurately in your inventory.'
            ],
          },
        ],
      },
      strategies: {
        title: 'Strategies for pricing your products',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Setting the <strong>right selling price</strong> is essential to ensure your business remains profitable. Consider the <strong>added value you provide</strong> to your customers, including the time, effort, and resources invested in developing your product, packaging, or delivering excellent customer service. These factors can justify a higher price. It’s also important to account for <strong>operational expenses</strong> such as marketing costs, subscription plans, tools you use, and sales commissions. Make sure your prices cover these expenses while generating a healthy profit margin.'
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              'You can also use <strong>practical pricing strategies</strong> like psychological pricing (for example, pricing at $9.99 instead of $10) to influence customer perception. Additionally, monitor <strong>competitor prices</strong> to gauge the market and adjust your pricing based on <strong>seasonal trends or demand fluctuations</strong>. These approaches will help you keep your prices competitive, boost sales, and enhance your brand’s perceived value.'
            ],
          },
        ],
      },
      how_to: {
        title: 'Steps to set your product prices',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Go to the Inventory module:'],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `Access the <em>Inventory</em> feature within the <a href=${process.env.STORES_WEBAPP_HOST} title="wicadu Assistant">wicadu Assistant</a>.`,
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Select a product:'],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Use the search tool or browse the list to find the product you want to <em>set a selling price</em> for. Click its name to open the details.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Start price assignment:'],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'If the product does not yet have a price, you will see the <b>"Assign"</b> button in the price section. Click it to enable edit mode.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Enter the selling price:'],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  "A field will appear where you can enter the product's selling price.",
                ],
              },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  '<strong>Note:</strong> If your store supports multiple currencies, a currency selector will appear. Multi-currency support is only available on paid plans.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Save your changes:'],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Click <b>"Save"</b> to apply the price. Once set, the product is ready to be displayed in your store and sold without issues.',
                ],
              },
            ],
          },
        ],
      },
      next_steps: {
        title: 'Next Steps',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Once you have assigned selling prices to your products, you can continue optimizing your store with these recommended actions:',
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Review products without assigned prices:'],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Make sure all products in your inventory have a <strong>selling price</strong> set so they can be displayed properly in your store and be <strong>available for sale</strong>.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Record costs with purchase orders:'],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Record your purchases to automatically update the unit cost of your products and keep your inventory up to date. Learn how to do this in 
                 <a href=${getUrlByPageId(PagesId.CreateManualStoreOrders, Languages.English)} title="Guide to creating manual orders in wicadu">this purchase orders guide</a>.`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Make your first sale:'],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Start selling your products once prices are assigned so customers can purchase them in your store. <em>Find out how here: 
                      <a href=${getUrlByPageId(PagesId.CreateManualUserOrders, Languages.English)} title="Guide to creating sales in wicadu">How to create an order</a></em>.`,
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      common_issues: {
        title: 'Common Issues',
        content_type: ContentType.UnorderedList,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>Price not applied to new orders</strong>: Check if the product has old prices stored in active orders. Update manually if necessary.',
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>Unable to select other currencies</strong>: The multi-currency feature is available only on paid plans. If you are on a free plan, this functionality will not be enabled in your store.',
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>Updated price not reflected in the online store</strong>: Please wait a few minutes as changes may take time to synchronize.',
            ],
          },
        ],
      }

    },
  },
  fr: {
    seo_title: 'Définir le prix de vos produits',
    seo_description:
      'Découvrez comment fixer des prix cohérents pour vos produits et les mettre à jour dans wicadu.',
    seo_keywords:
      'prix produits, stratégie de prix, marge, coûts, fixer prix produit, wicadu inventaire, e-commerce prix',
    page_id: PagesId.PricingYourProducts,
    breadcrumbs: {
      home: 'Accueil',
      items_home: 'Produits et inventaire',
      pricing_your_products: 'Définir le prix de vos produits',
    },
    article: {
      title: 'Définir le prix de vos produits',
      intended: [
        `<strong>Propriétaires</strong> et <strong>administrateurs</strong> qui gèrent la <em>tarification</em> et la <em>rentabilité</em> dans l’<a href=${process.env.STORES_WEBAPP_HOST}>assistant wicadu</a>.`,
        `<strong>Vendeurs</strong> ou responsables autorisés à <em>mettre à jour les prix</em> dans le catalogue produit.`,
      ],
      about: {
        title: 'À propos de la tarification',
        content_type: ContentType.Paragraph,
        content: [
          'Le <strong>prix</strong> d’un produit influence directement vos <strong>ventes</strong>, votre <strong>marge</strong> et la perception globale de votre boutique. Une stratégie de tarification cohérente dans <strong>wicadu</strong> vous aide à garder un catalogue clair, rentable et compétitif.',
          'Définir un prix adapté permet de couvrir les coûts, d’améliorer la rentabilité, de mieux suivre les mouvements de stock et d’offrir une expérience cohérente à vos clients. C’est aussi une étape importante pour relier vos <strong>produits</strong>, vos <strong>achats</strong> et vos futures <strong>ventes</strong>.',
        ],
      },
      before_begin: {
        title: 'Avant de commencer',
        content_type: ContentType.Fragment,
        content: [
          { content_type: ContentType.Paragraph, content: ['Avant de définir les <strong>prix de vente</strong> de vos produits, assurez-vous des éléments suivants :'] },
          {
            content_type: ContentType.UnorderedList,
            content: [
              { content_type: ContentType.Paragraph, content: ['<strong>Vérifiez que vos produits sont correctement enregistrés</strong>, avec leur nom, leur SKU, leur catégorie et les autres informations clés dans votre inventaire.'] },
              { content_type: ContentType.Paragraph, content: ['<strong>N’oubliez pas que le coût unitaire est attribué automatiquement</strong> à partir des enregistrements d’achats, mais que le prix de vente doit être défini manuellement pour chaque produit.'] },
              { content_type: ContentType.Paragraph, content: ['<strong>Définissez votre stratégie de prix</strong> en tenant compte des coûts d’acquisition, de la marge bénéficiaire souhaitée et des conditions du marché.'] },
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              'Ces vérifications préalables vous aideront à définir rapidement et avec précision les prix de vente dans votre inventaire.',
            ],
          },
        ],
      },
      strategies: {
        title: 'Stratégies pour fixer le prix de vos produits',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Définir correctement le <strong>prix de vente</strong> est essentiel pour garantir la rentabilité de votre activité. Pour cela, prenez en compte la <strong>valeur ajoutée que vous apportez</strong> à vos clients, notamment le temps, les efforts et les ressources investis dans le développement du produit, l’emballage ou la qualité du service client. Tous ces éléments peuvent justifier un prix plus élevé. Il est également important d’intégrer les <strong>coûts opérationnels</strong> tels que le marketing, votre abonnement, les outils que vous utilisez et les commissions sur les ventes. Assurez-vous que votre prix couvre ces coûts tout en générant une marge bénéficiaire suffisante.',
            ]
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              'Vous pouvez également appliquer des <strong>stratégies de prix pratiques</strong>, comme le prix psychologique (par exemple 9,99 € au lieu de 10 €), afin d’influencer la perception du client. Pensez aussi à surveiller les <strong>prix de vos concurrents</strong> pour mieux situer votre offre sur le marché, puis adaptez vos tarifs en fonction de la <strong>saisonnalité</strong> ou des <strong>variations de la demande</strong>. Ces approches vous aideront à rester compétitif, à améliorer vos ventes et à renforcer la valeur perçue de votre marque.',
            ],
          },
        ],
      },
      how_to: {
        title: 'Étapes pour définir le prix de vos produits',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Aller au module Inventaire :'] },
              { content_type: ContentType.Paragraph, content: [`Accédez à la fonctionnalité <em>Inventaire</em> dans l’<a href=${process.env.STORES_WEBAPP_HOST} title="Assistant wicadu">assistant wicadu</a>.`] },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Sélectionner un produit :'] },
              { content_type: ContentType.Paragraph, content: ['Utilisez l’outil de recherche ou parcourez la liste pour trouver le produit auquel vous souhaitez <em>attribuer un prix de vente</em>. Cliquez sur son nom pour ouvrir ses détails.'] },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Commencer l’attribution du prix :'] },
              { content_type: ContentType.Paragraph, content: ['Si le produit n’a pas encore de prix attribué, vous verrez le bouton <b>"Assigner"</b> dans la section du prix. Cliquez dessus pour activer le mode édition.'] },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Saisir le prix de vente :'] },
              { content_type: ContentType.Paragraph, content: ['Un champ s’affichera pour vous permettre de saisir le prix de vente du produit.'] },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  '<strong>Remarque :</strong> Si votre boutique prend en charge plusieurs devises, un sélecteur de devise apparaîtra. La gestion multidevise est uniquement disponible avec les forfaits payants.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Enregistrer les changements :'] },
              { content_type: ContentType.Paragraph, content: ['Cliquez sur <b>"Enregistrer"</b> pour appliquer le prix. Une fois attribué, le produit sera prêt à être affiché dans votre boutique et pourra être vendu sans problème.'] },
            ],
          },
        ],
      },
      next_steps: {
        title: 'Étapes suivantes',
        content_type: ContentType.Fragment,
        content: [
          { content_type: ContentType.Paragraph, content: ['Une fois que vous avez attribué des prix de vente à vos produits, vous pouvez continuer à optimiser votre boutique avec les actions recommandées suivantes :'] },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Vérifier les produits sans prix attribué :'] },
                  { content_type: ContentType.Paragraph, content: ['Assurez-vous que tous les produits de votre inventaire disposent d’un <strong>prix de vente</strong> défini afin qu’ils puissent être affichés correctement dans votre boutique et être <strong>disponibles à la vente</strong>.'] },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Enregistrer les coûts via des commandes d’achat :'] },
                  { content_type: ContentType.Paragraph, content: [`Enregistrez vos achats afin de mettre à jour automatiquement le coût unitaire de vos produits et de garder votre inventaire à jour. Découvrez comment faire dans 
                        <a href=${getUrlByPageId(PagesId.CreateManualStoreOrders, Languages.French)} title="Guide pour créer des commandes manuelles dans wicadu">ce guide sur les commandes d’achat</a>.`] },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Réaliser votre première vente :'] },
                  { content_type: ContentType.Paragraph, content: [`Commencez à vendre vos produits une fois que leurs prix sont attribués afin que vos clients puissent les acheter dans votre boutique. <em>Découvrez comment faire ici :
                      <a href=${getUrlByPageId(PagesId.CreateManualUserOrders, Languages.French)} title="Guide pour créer des ventes dans wicadu">Comment créer une commande</a></em>.`] },
                ],
              },
            ],
          },
        ],
      },
      common_issues: {
        title: 'Problèmes fréquents',
        content_type: ContentType.UnorderedList,
        content: [
          { content_type: ContentType.Paragraph, content: ['<strong>Le prix ne s’applique pas aux nouvelles commandes</strong> : vérifiez si le produit a d’anciens prix enregistrés dans des commandes actives. Mettez-les à jour manuellement si nécessaire.'] },
          { content_type: ContentType.Paragraph, content: ['<strong>Je ne peux pas sélectionner d’autres devises</strong> : la fonctionnalité multidevise est disponible uniquement avec les forfaits payants. Si vous utilisez un forfait gratuit, cette option ne sera pas activée dans votre boutique.'] },
          { content_type: ContentType.Paragraph, content: ['<strong>Le prix mis à jour n’apparaît pas dans la boutique en ligne</strong> : attendez quelques minutes, car les changements peuvent prendre un certain temps avant d’être synchronisés.'] },
        ],
      },
    },
  },
  pt: {},
}


export default globalTranslations
